import { useState } from "react";
import { Button, Chip, Divider, MenuItem, Paper, Stack, TextField as MuiTextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BlockIcon from "@mui/icons-material/Block";
import { DateField, Show, SimpleShowLayout, TextField, useNotify, useRecordContext, useRefresh } from "react-admin";

import { adminApi } from "../../dataProvider";
import { MODEL_LABEL, partnerStatus } from "./status";
import type { PartnerRecord } from "./List";

type FullPartner = PartnerRecord & {
  rate_source: string | null;
  level: string;
  parent_partner_id: string | null;
  balance_adjust_usd: number;
  identity_verified_manually: boolean;
  reject_reason: string | null;
  offer_version: string | null;
  offer_accepted_at: string | null;
  stats?: { turnover_rub: number; deals: number; clients: number; active_clients: number };
};

function StatusChip() {
  const record = useRecordContext<FullPartner>();
  if (!record) return null;
  const s = partnerStatus(record.status);
  return <Chip size="small" color={s.color} label={s.label} />;
}

function Money({ label, value }: { label: string; value: number | undefined }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ minWidth: 220 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{Number(value ?? 0).toFixed(2)} $</Typography>
    </Stack>
  );
}

function BalancePanel() {
  const record = useRecordContext<FullPartner>();
  if (!record) return null;
  const b = record.balance;
  const s = record.stats;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Баланс и оборот</Typography>
      <Stack spacing={0.5} sx={{ maxWidth: 320 }}>
        <Money label="Доступно" value={b?.available_usd} />
        <Money label="В холде" value={b?.hold_usd} />
        <Money label="Ожидание (сделка не исполнена)" value={b?.pending_usd} />
        <Money label="Выплачено" value={b?.paid_usd} />
        <Money label="Заработано всего" value={b?.total_earned_usd} />
      </Stack>
      {s && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Оборот: {Number(s.turnover_rub).toLocaleString("ru-RU")} ₽ · сделок: {s.deals} ·
            клиентов: {s.clients} (с оборотом: {s.active_clients})
          </Typography>
        </>
      )}
      {Number(b?.available_usd ?? 0) < 0 && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
          Отрицательный баланс — сделку отменили после выплаты. Долг гасится будущими начислениями.
        </Typography>
      )}
    </Paper>
  );
}

function FraudPanel() {
  const record = useRecordContext<FullPartner>();
  const notify = useNotify();
  const refresh = useRefresh();
  if (!record?.fraud_flags?.length) return null;

  const clear = async () => {
    try {
      await adminApi(`/admin/partners/${record.id}`, { method: "PUT", json: { clear_fraud_flags: true } });
      notify("Флаги сняты", { type: "success" });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    }
  };

  const LABEL: Record<string, string> = {
    same_passport: "Совпал паспорт с партнёром",
    same_wallet: "Совпал кошелёк вывода с партнёром",
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: "error.main" }}>
      <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Антифрод</Typography>
      <Stack spacing={0.5}>
        {record.fraud_flags.map((f, i) => (
          <Typography key={i} variant="body2">
            {LABEL[f.code] ?? f.code}
            {f.at ? ` · ${new Date(f.at).toLocaleString("ru-RU")}` : ""}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Начисления по таким сделкам помечены на ручную проверку и не выйдут из холда, пока
        оператор не подтвердит их в разделе «Начисления».
      </Typography>
      <Button size="small" sx={{ mt: 1 }} onClick={clear}>Снять флаги</Button>
    </Paper>
  );
}

function Actions() {
  const record = useRecordContext<FullPartner>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [markup, setMarkup] = useState("");
  const [source, setSource] = useState("");
  if (!record) return null;

  const call = async (path: string, json?: unknown, method: "POST" | "PUT" = "POST") => {
    setBusy(true);
    try {
      await adminApi(`/admin/partners/${record.id}${path}`, { method, json });
      notify("Готово", { type: "success" });
      setReason("");
      setMarkup("");
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Действия</Typography>
      <Stack spacing={1.5}>
        {record.status === "pending" && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={busy}
              onClick={() => call("/approve")}
            >
              Одобрить и выдать код
            </Button>
            <MuiTextField
              size="small"
              label="Причина отказа"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button color="error" startIcon={<CancelIcon />} disabled={busy} onClick={() => call("/reject", { reason })}>
              Отклонить
            </Button>
          </Stack>
        )}

        {record.status === "approved" && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <MuiTextField
              size="small"
              label="Причина блокировки"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button color="error" startIcon={<BlockIcon />} disabled={busy} onClick={() => call("/block", { reason })}>
              Заблокировать
            </Button>
          </Stack>
        )}

        {record.status === "blocked" && (
          <Button variant="contained" disabled={busy} onClick={() => call("/unblock")}>
            Разблокировать
          </Button>
        )}

        {record.model === "agent" && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MuiTextField
              size="small"
              type="number"
              label="Наценка агента, %"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              sx={{ width: 180 }}
            />
            <Button
              disabled={busy || markup === ""}
              onClick={() => call("", { markup_pct: Number(markup) }, "PUT")}
            >
              Задать наценку
            </Button>
          </Stack>
        )}

        {record.status === "approved" && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MuiTextField
              select
              size="small"
              label="Источник курса"
              value={source || record.rate_source || "profinance"}
              onChange={(e) => setSource(e.target.value)}
              sx={{ width: 220 }}
            >
              <MenuItem value="profinance">Profinance (USD/RUB)</MenuItem>
              <MenuItem value="rapira">Rapira (USDT/RUB)</MenuItem>
            </MuiTextField>
            <Button
              disabled={busy}
              onClick={() => call("", { rate_source: source || record.rate_source || "profinance" }, "PUT")}
            >
              Задать источник
            </Button>
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            disabled={busy}
            onClick={() => call("", { model: record.model === "agent" ? "referral" : "agent" }, "PUT")}
          >
            Сменить модель на «{record.model === "agent" ? "Реферал" : "Агент"}»
          </Button>
        </Stack>

        <Stack spacing={0.5}>
          <Button
            disabled={busy}
            onClick={() => call("", { identity_verified_manually: !record.identity_verified_manually }, "PUT")}
          >
            {record.identity_verified_manually
              ? "Снять ручное подтверждение личности"
              : "Подтвердить личность вручную"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Для нерезидентов: обычная верификация рассчитана на российский паспорт. Открывает
            только выплаты в крипте — рубли и валюта по-прежнему требуют полного пути с брокером.
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function PartnerShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="user_phone" label="Телефон" />
        <StatusChip />
        <TextField source="id" label="ID партнёра" />
        <TextField source="code" label="Код" />
        <TextField source="reject_reason" label="Причина отказа / блокировки" />
        <TextField source="offer_version" label="Версия оферты" />
        <DateField source="offer_accepted_at" label="Согласие с офертой" showTime />
        <DateField source="created_at" label="Заявка подана" showTime />
        <DateField source="approved_at" label="Одобрен" showTime />
        <ModelLine />
        <BalancePanel />
        <FraudPanel />
        <Actions />
      </SimpleShowLayout>
    </Show>
  );
}

function ModelLine() {
  const record = useRecordContext<FullPartner>();
  if (!record) return null;
  return (
    <Typography variant="body2">
      Модель: <b>{MODEL_LABEL[record.model] ?? record.model}</b>
      {record.model === "agent" ? ` · наценка +${Number(record.markup_pct)}%` : ""}
      {record.parent_partner_id ? " · приведён другим партнёром" : ""}
    </Typography>
  );
}
